/**
* @constructor
* Implements a minimal drop-down navigation menu with hamburger menu on mobile / tablet
*
* @param {Element} menu The menu element.
* @param {String} maxwidth the breakpoint for mobile view. Ex 50em
*/

export default function A11yMenu( menu, maxwidth ) {
	// Define value for keycodes
	const VK_ESC = 27; // escape key

	this.container = menu.parentElement; // The nav element
	this.trigger = this.container.querySelector( '.js-a11y-nav-trigger' ); // the hamburger button
	this.closeBtn = menu.querySelector( '.js-a11y-close-nav-trigger' ); // close button
	this.insideNav = menu.querySelector( '.a11y-nav' ); // the main ul
	this.currentSubMenuItem = false;
	this.firstFocusableElement = "";
	this.lastFocusableElement = "";
	this.lastFocusableParentElement = "";


	//non modale area for inert
	const main = document.getElementById( 'main' );
	const footer = document.getElementById( 'main-footer' );
	const siblings = document.querySelectorAll( 'header > *:not(nav):not(div), .brand , .skip-link' );


	let navIsShown = false;


	this.media = window.matchMedia( `(max-width:${maxwidth})` );
	this.media.addEventListener( 'change', ( e ) => {

		this.checkIfMobileNav( e );

	} );


	// Initialisation
	this.init = () => {

		this.menuSetup();

	};


	/* MOBILE NAV */
	this.openNav = () => {

		menu.style.transition = "";

		this.removeNavInert();
		this.makePageInert();

		document.body.setAttribute( 'data-burger-menu-status', 'opened' );
		this.trigger.setAttribute( 'aria-expanded', true );
		menu.removeAttribute( 'aria-hidden' );
		navIsShown = true;
		this.closeBtn.focus();

	};

	this.closeNav = () => {

		this.removePageInert();
		this.makeNavInert();

		this.trigger.setAttribute( 'aria-expanded', false );
		menu.setAttribute( 'aria-hidden', true );
		document.body.setAttribute( 'data-burger-menu-status', 'closed' );
		navIsShown = false;

		// close open menu if there is one
		if ( this.currentSubMenuItem ) {
			this.toggleSubMenu( this.currentSubMenuItem );
		}

		setTimeout( () => {
			menu.style.transition = "none";
		}, 500 );

		this.trigger.focus();

	};

	this.keyboardManager = ( e ) => {

		e.stopImmediatePropagation();

		// Check for TAB key press if sms nav opened
		if ( e.keyCode === 9 && navIsShown ) {

			

			// SHIFT + TAB
			if ( e.shiftKey ) {
				if ( ( !this.currentSubMenuItem ) || ( this.currentSubMenuItem && this.currentSubMenuItem !== this.lastFocusableParentElement ) ) {
					// If no submenu open, the last element is the last parent
					if ( document.activeElement === this.firstFocusableElement ) {
						e.preventDefault();
						this.lastFocusableParentElement.focus();
					}
				} else {
					if ( document.activeElement === this.firstFocusableElement ) {
						e.preventDefault();
						this.lastFocusableElement.focus();
					}
				}

				// TAB
			} else {
				if (  ( !this.currentSubMenuItem ) || ( this.currentSubMenuItem && this.currentSubMenuItem !== this.lastFocusableParentElement ) ) {
					// If no submenu open, the last element is the last parent
					if ( document.activeElement === this.lastFocusableParentElement ) {
						e.preventDefault();
						this.firstFocusableElement.focus();
					}
				} else {
					if ( document.activeElement === this.lastFocusableElement ) {
						e.preventDefault();
						this.firstFocusableElement.focus();
					}
				}

			}
		}

		// ESCAPE
		if ( e.keyCode === VK_ESC ) {

			// If a subMenu is opened and focus is inside the submenu
			if ( this.currentSubMenuItem
				&& this.focusIsInside( this.currentSubMenuItem.nextSibling ) ) {

				this.closeSubAndFocus( this.currentSubMenuItem );

			} else if ( navIsShown ) {

				this.closeNav();

			}

		}

	};


	this.makePageInert = () => {
		this.trigger.setAttribute( 'inert', '' );

		if ( main ) {
			main.setAttribute( "inert", "" );
		}
		if ( footer ) {
			footer.setAttribute( "inert", "" );
		}

		for ( var i = 0; i < siblings.length; i++ ) {
			siblings[i].setAttribute( "inert", "true" );
		}
	}

	this.removePageInert = () => {
		this.trigger.removeAttribute( 'inert' );

		if ( main ) {
			main.removeAttribute( "inert" );
		}
		if ( footer ) {
			footer.removeAttribute( "inert" );
		}

		for ( var i = 0; i < siblings.length; i++ ) {
			siblings[i].removeAttribute( "inert" );
		}
	}

	this.makeNavInert = () => {
		menu.setAttribute( "inert", "" );
	}

	this.removeNavInert = () => {
		menu.removeAttribute( "inert" );
	}

	/* SUB MENU */

	// SubMenu Open / Close Functions
	this.subMenuHandleClick = ( e ) => {

		const button = e.currentTarget;
		// close open menu if there is one
		if ( this.currentSubMenuItem && button !== this.currentSubMenuItem ) {
			this.toggleSubMenu( this.currentSubMenuItem );
		}

		this.toggleSubMenu( button );

	};

	this.toggleSubMenu = ( button ) => {

		

		const submenu = button.nextSibling;

		if ( button.getAttribute( 'aria-expanded' ) === 'false' ) {

			button.setAttribute( 'aria-expanded', true );
			submenu.setAttribute( 'aria-hidden', false );
			submenu.removeAttribute( 'inert' );
			this.currentSubMenuItem = button;

		} else {

			button.setAttribute( 'aria-expanded', false );
			submenu.setAttribute( 'aria-hidden', true );
			submenu.setAttribute( 'inert', '' );
			this.currentSubMenuItem = false;

		}


	};

	this.closeSubAndFocus = ( currentSub ) => {
		const button = currentSub;
		this.toggleSubMenu( currentSub );
		button.focus();
	};

	this.subMenuHandleBlur = () => {

		if ( this.currentSubMenuItem && !navIsShown ) {
			const button = this.currentSubMenuItem;
			this.toggleSubMenu( button );
		}

	};

	/* FIN SUB MENU */

	this.closeOnOutsideClick = ( e ) => {

		const target = e.target;

		// If a subMenu is open, and click is outside, close it
		if ( this.currentSubMenuItem && !this.currentSubMenuItem.parentNode.contains( target ) ) {
			this.closeSubAndFocus( this.currentSubMenuItem );
		}

	};

	// Modify Menu Markup & Bind Listeners
	this.menuSetup = () => {

		// Check if the menu is in mobile or desktop mode and setup aria
		this.checkIfMobileNav( this.media );

		// SubMenus
		// On transforme les span avec enfants en boutons
		this.insideNav.querySelectorAll( 'ul' ).forEach( ( submenu ) => {

			if ( typeof submenu !== 'undefined' ) {
				const menuItem = submenu.parentElement.parentElement.parentElement;
				const lastSubMenuLink = submenu.lastElementChild.firstElementChild;
				const button = this.convertSpanToButton( menuItem );

				// Param = the sub-menu-block element and the ul
				this.setSubMenuAria( submenu.parentElement.parentElement, submenu );

				// Bind event listener to button
				button.addEventListener( 'click', this.subMenuHandleClick );
				lastSubMenuLink.addEventListener( 'blur', this.subMenuHandleBlur );

				// Handle if escape key is pressed
				// menuItem.addEventListener('keydown', this.subMenuHandleEscape);

			}

		} );

		// If user clicks anywhere outside the navigation, close all open dropdowns and Nav if mobile version
		window.addEventListener( "click", this.closeOnOutsideClick );

		// Event listener for trigger and close Button
		this.trigger.addEventListener( 'click', this.openNav );
		this.closeBtn.addEventListener( 'click', this.closeNav );

		// Setup focus trap
		const focusableElements = menu.querySelectorAll( 'a, button' );
		const focusableElementsParents = menu.querySelectorAll( '.a11y-nav > li > a, .a11y-nav > li > button' );

		this.firstFocusableElement = focusableElements[0];
		this.lastFocusableElement = focusableElements[focusableElements.length - 1];
		this.lastFocusableParentElement = focusableElementsParents[focusableElementsParents.length - 1];


		// Bind keyboard listener to nav
		this.container.addEventListener( 'keydown', this.keyboardManager.bind( this ) );

	};


	this.checkIfMobileNav = ( e ) => {

		if ( e.matches ) {
			// Switch to mobile menu
			this.trigger.removeAttribute( 'hidden' );
			this.trigger.setAttribute( 'aria-expanded', false );
			menu.setAttribute( 'aria-hidden', true );
			menu.setAttribute( 'inert', '' );
			menu.setAttribute( 'role', 'dialog' );
			menu.setAttribute( 'aria-labelledby', 'nav-toggle' );
			menu.style.transition = "none";

			navIsShown = false;

		} else {
			// Switch to desktop
			this.trigger.setAttribute( 'hidden', '' );
			this.trigger.setAttribute( 'aria-expanded', true );
			menu.removeAttribute( 'aria-hidden' );
			menu.removeAttribute( 'inert' );
			menu.removeAttribute( 'role' );
			menu.removeAttribute( 'aria-labelledby' );
		}

	};


	this.convertSpanToButton = ( menuItem ) => {

		const span = menuItem.getElementsByTagName( 'span' )[0];
		const spanAtts = span.attributes;
		const button = document.createElement( 'button' );
		
		if ( span !== null && span.getAttribute( 'tabindex' ) === '0' ) {

			button.textContent = span.textContent;
			for (const element of spanAtts) {
				const attr = element;
				// Transfer the attributes other than href.
				if ( attr.name !== 'href' ) {
					button.setAttribute( attr.name, attr.value );
				}
			}

			// SetUp Aria for the button
			button.setAttribute( 'aria-expanded', false );

			menuItem.replaceChild( button, span );
		}
		return button;

	};

	this.setSubMenuAria = ( submenu, ulNav ) => {

		ulNav.style.transition = 'none';
		submenu.setAttribute( 'aria-hidden', true );
		submenu.setAttribute( 'inert', '' );

		setTimeout( () => {
			ulNav.style.transition = '';
		}, 500 );

	};

	// Private functions

	this.isEltHidden = ( elem ) => {

		const styles = window.getComputedStyle( elem );
		return ( styles.display === 'none' || styles.visibility === 'hidden' );

	};

	this.focusIsInside = ( element ) => {
		return element.contains( document.activeElement );
	};
}